// Route: /tests — список тестов + единый тулбар + grid/list.
// URL state: ?preset, ?sort, ?q, ?demo. Вид — в localStorage (useViewMode).
//
// См. docs/ui-spec/quizzes.list.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { Input } from "@studyhub/ui/components/input";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@studyhub/ui/components/responsive-dialog";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { toast } from "@studyhub/ui/toast";
import { IconChecklist, IconPlus, IconSparkles } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CollectionFilterSelect } from "@/components/library/collection-filter-select";
import { useCreateQuiz, useQuizzesList } from "@/components/quizzes/_api";
import { GenerateDialog } from "@/components/quizzes/generate-dialog/generate-dialog";
import { FiltersControls, type QuizFilters } from "@/components/quizzes/list/filters-bar";
import { QuizCard, QuizCardSkeleton } from "@/components/quizzes/list/quiz-card";
import {
	CreateEmptyTile,
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useRealtime } from "@/hooks/use-realtime";
import { useViewMode } from "@/hooks/use-view-mode";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";

const QUIZ_REALTIME_KEYS: ReadonlyArray<ReadonlyArray<unknown>> = [["quizzes"]];

const TILTS = [-1.2, 0.8, -0.6, 1, -0.9, 0.6] as const;

const searchSchema = z.object({
	preset: z.string().optional(),
	sort: z.enum(["updated", "name", "score"]).optional(),
	q: z.string().optional(),
	demo: z.enum(["default", "loading", "empty", "error"]).optional(),
});

export const Route = createFileRoute("/tests/")({
	validateSearch: tolerantSearch(searchSchema),
	component: QuizzesListPage,
});

function QuizzesListPage(): React.ReactElement {
	const { t } = useTranslation("quizzes");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const intro = useSectionIntro("quizzes");
	const [generateOpen, setGenerateOpen] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [newName, setNewName] = useState("");
	const [view, setView] = useViewMode("tests");
	const createQuiz = useCreateQuiz();
	useRealtime({ channel: "quiz:ready", invalidateQueryKeys: QUIZ_REALTIME_KEYS });
	useRealtime({ channel: "quiz:generation_failed", invalidateQueryKeys: QUIZ_REALTIME_KEYS });

	async function handleCreateEmpty(): Promise<void> {
		const name = newName.trim();
		if (!name) return;
		try {
			const res = await createQuiz.mutateAsync({ name });
			setCreateOpen(false);
			setNewName("");
			void navigate({ to: "/tests/edit/$id", params: { id: res.quiz.id } });
		} catch {
			toast.error(t("list.errorTitle"));
		}
	}

	const filters: QuizFilters = useMemo(
		() => ({ q: search.q, preset: search.preset, sort: search.sort ?? "updated" }),
		[search],
	);

	function updateFilters(next: QuizFilters): void {
		void navigate({
			search: (prev) => ({ ...prev, q: next.q, preset: next.preset, sort: next.sort }),
			replace: true,
		});
	}

	const query = useQuizzesList();
	const items = query.data?.items ?? [];

	const filtered = useMemo(() => {
		let list = items;
		if (filters.q) {
			const q = filters.q.toLowerCase();
			list = list.filter((it) => it.name.toLowerCase().includes(q));
		}
		if (filters.preset) list = list.filter((it) => it.examPreset === filters.preset);
		if (filters.sort === "name") {
			list = [...list].sort((a, b) => a.name.localeCompare(b.name));
		} else if (filters.sort === "score") {
			list = [...list].sort((a, b) => (b.lastScore ?? 0) - (a.lastScore ?? 0));
		} else {
			list = [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
		}
		return list;
	}, [items, filters]);

	const isEmpty = !query.isLoading && !query.isError && items.length === 0;
	const noResults = !query.isLoading && !query.isError && items.length > 0 && filtered.length === 0;
	const filtersActive = Boolean(filters.preset) || filters.sort !== "updated";

	const containerClass = view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconChecklist />}
				category="quiz"
				title={t("title")}
				description={t("list.subtitle", { count: items.length })}
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button
							variant="outline"
							className="cursor-pointer"
							onClick={() => setCreateOpen(true)}
						>
							<IconPlus size={16} />
							<span className="ml-1 hidden md:inline">{t("list.createEmpty")}</span>
						</Button>
						<Button onClick={() => setGenerateOpen(true)} className="cursor-pointer">
							<IconSparkles size={16} className="mr-1" />
							<span className="hidden sm:inline">{t("list.create")}</span>
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			<ListToolbar
				search={{
					value: filters.q ?? "",
					onChange: (q) => updateFilters({ ...filters, q: q || undefined }),
					placeholder: t("list.search"),
				}}
				filters={
					<>
						<FiltersControls value={filters} onChange={updateFilters} />
						<CollectionFilterSelect />
					</>
				}
				filtersSheet={
					<>
						<FiltersControls value={filters} onChange={updateFilters} stacked />
						<CollectionFilterSelect stacked />
					</>
				}
				filtersActive={filtersActive}
				view={view}
				onViewChange={setView}
			/>

			{query.isError && (
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorDescription")}
					action={
						<Button
							variant="outline"
							onClick={() => void query.refetch()}
							className="cursor-pointer"
						>
							{t("list.retry")}
						</Button>
					}
				/>
			)}

			{query.isLoading && (
				<div className={containerClass}>
					{Array.from({ length: 6 }).map((_, i) => (
						<QuizCardSkeleton key={i} />
					))}
				</div>
			)}

			{isEmpty && (
				<EmptyState
					title={t("list.empty")}
					description={t("list.emptyDescription")}
					icon={<IconChecklist className="size-12 text-muted-foreground/40" />}
					action={
						<Button onClick={() => setGenerateOpen(true)} className="cursor-pointer">
							<IconSparkles size={14} className="mr-1" />
							{t("list.create")}
						</Button>
					}
				/>
			)}

			{noResults && (
				<EmptyState
					title={t("common:state.noResults")}
					icon={<IconChecklist className="size-12 text-muted-foreground/40" />}
				/>
			)}

			{!query.isLoading && !query.isError && filtered.length > 0 && (
				<div className={containerClass}>
					{filtered.map((q, i) => (
						<QuizCard
							key={q.id}
							quiz={q}
							view={view}
							tilt={view === "grid" ? TILTS[i % TILTS.length] : undefined}
						/>
					))}
					{/* Тактильная плитка «Создать пустой» — только в grid (как в /flashcards). */}
					{view === "grid" && (
						<CreateEmptyTile
							category="quiz"
							icon={<IconChecklist />}
							label={t("list.createEmpty")}
							onClick={() => setCreateOpen(true)}
						/>
					)}
				</div>
			)}

			<GenerateDialog open={generateOpen} onOpenChange={setGenerateOpen} />

			<ResponsiveDialog open={createOpen} onOpenChange={setCreateOpen}>
				<ResponsiveDialogContent className="sm:max-w-md">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>{t("list.createEmptyTitle")}</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>
					<Input
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						placeholder={t("edit.nameLabel")}
						maxLength={200}
						autoFocus={!IS_MOBILE_PLATFORM}
						onKeyDown={(e) => {
							if (e.key === "Enter" && newName.trim()) void handleCreateEmpty();
						}}
					/>
					<ResponsiveDialogFooter>
						<Button
							variant="ghost"
							onClick={() => setCreateOpen(false)}
							disabled={createQuiz.isPending}
							className="cursor-pointer"
						>
							{t("generate.cancel")}
						</Button>
						<Button
							loading={createQuiz.isPending}
							onClick={() => void handleCreateEmpty()}
							disabled={!newName.trim() || createQuiz.isPending}
							className="cursor-pointer"
						>
							{t("list.createEmptyAction")}
						</Button>
					</ResponsiveDialogFooter>
				</ResponsiveDialogContent>
			</ResponsiveDialog>
		</div>
	);
}
